import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import { tokenStore } from '@/lib/api/token-store';
import { env } from '@/lib/env';
import {
  createRealtimeClient,
  socketIoTransport,
  type RealtimeClient,
  type RealtimeEventName,
  type RealtimeEventPayload,
  type RealtimeTransport,
} from '@/lib/realtime';
import { useAuth } from '@/providers/auth-provider';

/** Default is an inert client: it accepts subscriptions and nothing ever feeds it. That keeps
 *  `useRealtimeEvent` a no-op outside the provider, so the hundreds of existing test files that
 *  render shell chrome never acquire a socket (ticket 11, AC8). */
const RealtimeContext = createContext<RealtimeClient>(createRealtimeClient());

type RealtimeProviderProps = {
  children: ReactNode;
  /** Injected by tests (ADR-0025); production uses socket.io. */
  transport?: RealtimeTransport;
  /** Unset ⇒ no connection at all. */
  url?: string;
};

export function RealtimeProvider({
  children,
  transport = socketIoTransport,
  url = env.VITE_REALTIME_URL,
}: RealtimeProviderProps) {
  const { status } = useAuth();
  const client = useMemo(() => createRealtimeClient(), []);
  const token = useSyncExternalStore(tokenStore.subscribe, tokenStore.getAccessToken);

  useEffect(() => {
    // `unknown` connects nothing (ADR-0025). A rotated token re-runs this effect, so the
    // connection is rebuilt with the fresh credential instead of outliving it.
    // ponytail: reconnect rather than in-place re-auth — an event fired in the gap is lost,
    // which socket.io would not replay anyway. Revisit only if BE buffers per socket.
    if (status !== 'authenticated' || !token || !url) return;
    const connection = transport({ url, token, onEvent: client.receive });
    return () => connection.close();
  }, [status, token, url, transport, client]);

  return <RealtimeContext.Provider value={client}>{children}</RealtimeContext.Provider>;
}

/** Subscribe to one typed server event for as long as the caller is mounted. */
export function useRealtimeEvent<E extends RealtimeEventName>(
  event: E,
  handler: (payload: RealtimeEventPayload<E>) => void,
): void {
  const client = useContext(RealtimeContext);
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => client.on(event, (payload) => handlerRef.current(payload)), [client, event]);
}
